// Modules
import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Formik, Form, Field, FormikHelpers } from 'formik'
import { Button, TextField, Typography } from '@dasa-health/components-react'
import { SystemBoldViewon, SystemBoldViewoff } from '@dasa-health/icons'
import { LoginCIPSchema } from 'validators/schema'

// Application
import { checkUniqueID, maskBirthDate } from 'utils'
import { LoginType } from 'typings/auth'
import { useCPFLoginForm } from 'redesign/hooks/useCPFLoginForm'

// Components
import { StyledForm } from './style'
import { ROUTES_PATH } from 'constants/routesPath'
import { SignUpModal } from 'redesign/components/molecules/SignUpModal'
import { SignUpHelpTextModal } from 'redesign/components/atoms/SignUpHelpTextModal'
import { usePhasedLogin } from 'redesign/hooks/usePhasedLogin'

export interface LoginInitial {
    cpf: string
    password: string
    birthdate: string
    recaptchaToken: string | null
}
interface Props {
    initialValues: LoginInitial
    handleSubmit: (values: LoginInitial, bag: FormikHelpers<LoginInitial>) => void
    isSubmitting: boolean
    identifier: LoginType
    setIdentifier: (value: LoginType) => void
}

type Event = React.ChangeEvent<HTMLInputElement>
type SetValue = (field: string, value: string, shouldValidate?: boolean | undefined) => void

export const LoginForm: React.FC<Props> = ({ initialValues, handleSubmit, isSubmitting, identifier, setIdentifier }) => {
    const [viewPassword, setViewPassword] = useState(false)
    const [showModal, setShowModal] = useState(false)
    const { title, redirectToLogin, values: valueStep } = useCPFLoginForm()
    const { dispatchAnalytics } = usePhasedLogin()

    const handleViewPassword = () => {
        setViewPassword(!viewPassword)
    }

    const handleFieldBlur = (event: Event, handleBlur: Function, setFieldValue: SetValue) => {
        const { target } = event
        const { value } = target
        const option = checkUniqueID(value)
        setIdentifier(option)
        handleBlur(event)
    }

    useEffect(() => {
        const step = valueStep.showPasswordField ? 'insert_password' : 'insert_cip'
        dispatchAnalytics(step)
    }, [dispatchAnalytics, valueStep.showPasswordField])

    const handleFieldChange = (event: Event, handleChange: Function) => {
        const { target } = event
        const { value } = target
        const removedLetters = value.replace(/[^0-9]/g, '')
        const removedDots = removedLetters.split('.').join('')
        const final = removedDots.split('-').join('')
        event.target.value = final
        if (identifier === 'empty') {
            const option = checkUniqueID(value)
            setIdentifier(option)
        }
        handleChange(event)
    }

    const handleBirthDate = (event: Event, handleChange: Function) => {
        const { target } = event
        const { value } = target
        event.target.value = maskBirthDate(value)
        handleChange(event)
    }

    return (
        <Formik initialValues={initialValues} onSubmit={(values, bag) => handleSubmit(values, bag)} validationSchema={LoginCIPSchema}>
            {({ values, touched, errors, handleChange, handleBlur, setFieldValue, isValid, dirty }) => (
                <StyledForm>
                    <SignUpModal
                        setShowModal={() => {
                            setShowModal(!showModal)
                        }}
                        showModal={showModal}
                        title="Por que não estou conseguindo acessar?"
                    >
                        <p>
                            Se o número do seu protocolo (CIP) tiver uma barra, por exemplo, 123456/89, digite apenas os números que
                            estão antes da barra. Exemplo: 123456891
                        </p>

                        <p>
                            Ou acesse via CPF para ter acesso completo às funcionalidades do Nav Dasa.{' '}
                            <Link onClick={e => redirectToLogin(e)} to={ROUTES_PATH.SIGN_IN}>
                                Acesse aqui
                            </Link>
                            .
                        </p>
                    </SignUpModal>
                    <Typography as="h1" mb={16} size={4} sx={{ fontWeight: '700' }}>
                        {title}
                    </Typography>

                    <Typography as="h2" mb={2} size={1} weight={400}>
                        Me informe os dados que constam no protocolo (CIP) que você recebeu ao fazer seus exames:
                    </Typography>

                    <SignUpHelpTextModal onClick={() => setShowModal(true)}>
                        Por que não estou conseguindo acessar?
                    </SignUpHelpTextModal>

                    <Form data-testid="login-form">
                        <div className="form__login">
                            <Field
                                as={TextField}
                                className="form__login__field"
                                data-testid="username-input"
                                helperText={touched.cpf && errors.cpf ? errors.cpf : ''}
                                inputmode="numeric"
                                label="Protocolo (CIP) ou FAP"
                                name="cpf"
                                onBlur={(e: Event) => handleFieldBlur(e, handleBlur, setFieldValue)}
                                onChange={(e: Event) => handleFieldChange(e, handleChange)}
                                type="tel"
                                value={values.cpf}
                                variant={touched.cpf && errors.cpf ? 'error' : undefined}
                            />
                            <Field
                                as={TextField}
                                className="form__login__field"
                                data-testid="password-input"
                                helperText={touched.password && errors.password ? errors.password : ''}
                                label="Senha"
                                name="password"
                                trailingIcon={
                                    viewPassword ? (
                                        <SystemBoldViewoff data-testid="change-type-input" onClick={handleViewPassword} />
                                    ) : (
                                        <SystemBoldViewon data-testid="change-type-input" onClick={handleViewPassword} />
                                    )
                                }
                                type={viewPassword ? 'text' : 'password'}
                                variant={touched.password && errors.password ? 'error' : undefined}
                            />
                            <Field
                                as={TextField}
                                className="form__login__field"
                                data-testid="birthdate-input"
                                helperText={touched.birthdate && errors.birthdate ? errors.birthdate : ''}
                                label="Data de nascimento"
                                name="birthdate"
                                onChange={(e: Event) => handleBirthDate(e, handleChange)}
                                type="text"
                                variant={touched.birthdate && errors.birthdate ? 'error' : undefined}
                            />
                            <Button
                                data-testid="submit-button"
                                disabled={(!initialValues.cpf && (!isValid || !dirty)) || isSubmitting}
                                isLoading={isSubmitting}
                                size="default"
                                sx={{ marginTop: '2.5rem', marginBottom: '0.875rem' }}
                                type="submit"
                                variant="primary"
                            >
                                Continuar
                            </Button>
                            <Button
                                data-testid="signin-button"
                                onClick={e => redirectToLogin(e)}
                                size="compact"
                                type="button"
                                variant="tertiary"
                            >
                                Acessar com CPF
                            </Button>
                        </div>
                    </Form>
                </StyledForm>
            )}
        </Formik>
    )
}
